import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CircleCheck,
  Clock,
  Pencil,
  RotateCcw,
  Timer,
  Trash2,
  UserPlus,
} from 'lucide-react';
import {
  TASK_COMPLEXITY_META,
  formatDuration,
  formatTaskReference,
  type TaskDetail,
} from '@ssn/shared';
import { useAssignTask, useDeleteTask, useReferenceData, useRestoreTask, useTask, useUpdateTask } from '@/lib/queries';
import { useSession } from '@/session/SessionProvider';
import { Panel } from '@/components/ui/Panel';
import { Button, IconButton, LinkButton } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Badge';
import { Callout, ErrorState, InlineError, LoadingBlock } from '@/components/ui/Feedback';
import { Modal } from '@/components/ui/Modal';
import { Select, TextArea, TextInput } from '@/components/ui/Form';
import { Tabs } from '@/components/ui/Tabs';
import { UserChip } from '@/components/ui/Avatar';
import { PriorityBadge, StatusBadge, WorkflowPipeline } from '@/components/domain/status';
import { TaskActions } from '@/components/domain/TaskActions';
import { Conversation } from '@/components/domain/Conversation';
import { ActivityTimeline } from '@/components/domain/ActivityTimeline';
import { Attachments } from '@/components/domain/Attachments';
import { formatDateTime, formatRelativeTime } from '@/lib/format';

type Panel = 'conversation' | 'activity' | 'reviews';

/**
 * The task detail page (§23) and the director review experience (§24).
 *
 * It answers, in this order and without scrolling for the first three: what is
 * this task, who owns it, what stage is it at, and what needs to happen next.
 */
export function TaskDetailPage({ reviewMode = false }: { reviewMode?: boolean }) {
  const { reference: referenceParam } = useParams<{ reference: string }>();
  const reference = Number(referenceParam);
  const { session } = useSession();

  const [panel, setPanel] = useState<Panel>(reviewMode ? 'conversation' : 'conversation');
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, isPending, error, refetch } = useTask(reference, { enabled: Number.isFinite(reference) });
  const restore = useRestoreTask(reference);

  if (!Number.isFinite(reference)) return <ErrorState error={new Error('That is not a valid task number.')} />;
  if (isPending) return <LoadingBlock label="Loading task" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data || !session) return null;

  const task = data.task;
  const permissions = task.viewerPermissions;

  return (
    <>
      {reviewMode && (
        <Link
          to="/review"
          className="mb-3 inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-accent-text"
        >
          <ArrowLeft aria-hidden className="size-3.5" />
          Back to the review queue
        </Link>
      )}

      {task.isDeleted && (
        <Callout
          tone="critical"
          className="mb-4"
          icon={<Trash2 aria-hidden className="size-3.5" />}
          title="This task is deleted"
        >
          Deleted by {task.deletedBy?.displayName ?? 'someone'} on {formatDateTime(task.deletedAt)}.
          {task.deletionReason && <> Reason: {task.deletionReason}</>}
          {permissions.canRestore && (
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              loading={restore.isPending}
              onClick={() => restore.mutate({ reason: null })}
              icon={<RotateCcw aria-hidden className="size-3.5" />}
            >
              Restore this task
            </Button>
          )}
        </Callout>
      )}

      {/* ---- Header ---------------------------------------------------- */}
      <header className="mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-xs text-text-subtle">{formatTaskReference(task.reference)}</p>
            <h1 className="mt-0.5 text-xl leading-snug font-semibold text-text">{task.title}</h1>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            <PriorityBadge priority={task.priority} />
            <StatusBadge status={task.status} />
            {permissions.canEdit && (
              <IconButton label="Edit this task" onClick={() => setEditing(true)}>
                <Pencil aria-hidden className="size-4" />
              </IconButton>
            )}
            {permissions.canAssign && (
              <IconButton label="Reassign this task" onClick={() => setAssigning(true)}>
                <UserPlus aria-hidden className="size-4" />
              </IconButton>
            )}
            {permissions.canDelete && (
              <IconButton label="Delete this task" onClick={() => setDeleting(true)}>
                <Trash2 aria-hidden className="size-4" />
              </IconButton>
            )}
          </div>
        </div>
      </header>

      {/* ---- Status timeline ------------------------------------------- */}
      <Panel className="mb-5" bodyClassName="py-3">
        <WorkflowPipeline status={task.status} />

        {task.availableActions.length > 0 && (
          <div className="mt-3.5 border-t border-border pt-3.5">
            <TaskActions task={task} />
          </div>
        )}
      </Panel>

      {task.status === 'CHANGES_REQUESTED' && task.reviews.length > 0 && (
        <Callout
          tone="warning"
          className="mb-5"
          icon={<AlertTriangle aria-hidden className="size-3.5" />}
          title={`Changes requested by ${task.reviews[task.reviews.length - 1]!.reviewer.displayName}`}
        >
          <p className="whitespace-pre-wrap">{task.reviews[task.reviews.length - 1]!.comment}</p>
          {task.reviews[task.reviews.length - 1]!.requiredChanges && (
            <p className="mt-1.5 whitespace-pre-wrap">
              {task.reviews[task.reviews.length - 1]!.requiredChanges}
            </p>
          )}
        </Callout>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        {/* ---- Main column -------------------------------------------- */}
        <div className="space-y-5 lg:col-span-2">
          <Panel title="Description">
            {task.description.trim() ? (
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-text">{task.description}</p>
            ) : (
              <p className="text-sm text-text-subtle italic">No description was provided.</p>
            )}
          </Panel>

          <Panel flush>
            <Tabs
              label="Task detail sections"
              value={panel}
              onChange={setPanel}
              items={[
                { value: 'conversation', label: 'Conversation', count: task.commentCount },
                { value: 'activity', label: 'Activity', count: task.activity.length },
                { value: 'reviews', label: 'Review history', count: task.reviews.length },
              ]}
              className="px-3"
            />

            <div className="p-4">
              {panel === 'conversation' && (
                <Conversation
                  reference={task.reference}
                  comments={task.comments}
                  currentUser={session.user}
                  canComment={permissions.canComment}
                  canModerate={session.permissions.includes('comment:delete:any')}
                />
              )}
              {panel === 'activity' && <ActivityTimeline activity={task.activity} />}
              {panel === 'reviews' && <ReviewHistory task={task} />}
            </div>
          </Panel>
        </div>

        {/* ---- Sidebar ------------------------------------------------- */}
        <div className="space-y-5">
          <Panel title="Task information">
            <dl className="space-y-3 text-sm">
              <Detail label="Assigned developer">
                <UserChip user={task.assignee} size="xs" showRole />
              </Detail>
              <Detail label="Created by">
                <UserChip user={task.creator} size="xs" />
              </Detail>
              <Detail label="Project">
                {task.project ? <Chip>{task.project.name}</Chip> : <Muted>None</Muted>}
              </Detail>
              <Detail label="Category">
                {task.category ? <Chip>{task.category.name}</Chip> : <Muted>None</Muted>}
              </Detail>
              <Detail label="Complexity">
                {task.complexity ? (
                  <span className="text-text">{TASK_COMPLEXITY_META[task.complexity].label}</span>
                ) : (
                  <Muted>Not sized</Muted>
                )}
              </Detail>
              <Detail label="Estimated">
                {task.estimatedHours !== null ? (
                  <span className="text-text tabular">{task.estimatedHours} h</span>
                ) : (
                  <Muted>No estimate</Muted>
                )}
              </Detail>
              <Detail label="Deadline">
                {task.deadline ? (
                  <span className={task.metrics.isOverdue ? 'font-medium text-critical-tone' : 'text-text'}>
                    {formatDateTime(task.deadline)}
                    {task.metrics.isOverdue && ` · overdue by ${formatDuration(task.metrics.overdueByMs)}`}
                  </span>
                ) : (
                  <Muted>No deadline</Muted>
                )}
              </Detail>
              {task.tags.length > 0 && (
                <Detail label="Tags">
                  <span className="flex flex-wrap gap-1">
                    {task.tags.map((tag) => (
                      <Chip key={tag}>{tag}</Chip>
                    ))}
                  </span>
                </Detail>
              )}
            </dl>
          </Panel>

          <Panel title="Time" description="Measured from the workflow, never entered by hand.">
            <dl className="space-y-2.5 text-sm">
              <Metric label="Created" value={formatRelativeTime(task.createdAt)} title={task.createdAt} />
              <Metric label="Assigned → claimed" value={formatDuration(task.metrics.assignedToClaimMs)} />
              <Metric label="Claimed → started" value={formatDuration(task.metrics.claimToStartMs)} />
              <Metric label="Started → completed" value={formatDuration(task.metrics.startToCompleteMs)} />
              {task.metrics.pausedMs > 0 && (
                <Metric
                  label="Paused (not counted)"
                  value={formatDuration(task.metrics.pausedMs)}
                  hint="Time blocked or awaiting information is excluded from cycle time."
                />
              )}
              <Metric label="Active working time" value={formatDuration(task.metrics.activeWorkMs)} strong />
              <Metric label="Waiting for review" value={formatDuration(task.metrics.submitToDecisionMs)} />
              {task.metrics.currentWaitMs !== null && (
                <Metric
                  label="Waiting right now"
                  value={formatDuration(task.metrics.currentWaitMs)}
                  icon={<Timer aria-hidden className="size-3.5" />}
                  strong
                />
              )}
              <Metric label="Review rounds" value={String(task.metrics.reviewCycles)} />
            </dl>
          </Panel>

          <Panel title="Attachments" description={`${task.attachments.length} file(s).`}>
            <Attachments
              reference={task.reference}
              attachments={task.attachments}
              currentUser={session.user}
              canUpload={permissions.canUploadAttachment}
              canDeleteAny={session.permissions.includes('attachment:delete:any')}
            />
          </Panel>
        </div>
      </div>

      <EditTaskModal task={task} open={editing} onClose={() => setEditing(false)} />
      <AssignModal task={task} open={assigning} onClose={() => setAssigning(false)} />
      <DeleteModal task={task} open={deleting} onClose={() => setDeleting(false)} />
    </>
  );
}

/* ---- Small pieces -------------------------------------------------- */

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-xs text-text-subtle">{label}</dt>
      <dd className="min-w-0 text-right">{children}</dd>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-text-subtle">{children}</span>;
}

function Metric({
  label,
  value,
  hint,
  title,
  icon,
  strong,
}: {
  label: string;
  value: string;
  hint?: string;
  title?: string;
  icon?: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-text-subtle">
        {label}
        {hint && <span className="mt-0.5 block text-[11px] text-text-subtle/80">{hint}</span>}
      </dt>
      <dd
        title={title}
        className={`inline-flex shrink-0 items-center gap-1 tabular ${
          strong ? 'font-medium text-text' : 'text-text-muted'
        }`}
      >
        {icon}
        {value}
      </dd>
    </div>
  );
}

function ReviewHistory({ task }: { task: TaskDetail }) {
  if (task.reviews.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-text-subtle">
        This task has not been reviewed yet.
      </p>
    );
  }

  return (
    <ol className="space-y-3">
      {task.reviews.map((review) => {
        const approved = review.decision === 'APPROVED';
        return (
          <li
            key={review.id}
            className={`rounded-[--radius-panel] border p-3 ${
              approved ? 'border-success-border bg-success-soft' : 'border-warning-border bg-warning-soft'
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${approved ? 'text-success-tone' : 'text-warning-tone'}`}>
                {approved ? (
                  <CircleCheck aria-hidden className="size-4" />
                ) : (
                  <AlertTriangle aria-hidden className="size-4" />
                )}
                {approved ? 'Approved' : 'Changes requested'}
              </span>
              <span className="text-xs text-text-muted">Round {review.cycle}</span>
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-text-muted">
                <Clock aria-hidden className="size-3" />
                waited {formatDuration(review.waitMs)}
              </span>
            </div>

            <p className="mt-1.5 text-xs text-text-muted">
              {review.reviewer.displayName} · {formatDateTime(review.decidedAt)}
            </p>

            {review.comment && (
              <p className="mt-2 text-sm whitespace-pre-wrap text-text">{review.comment}</p>
            )}
            {review.requiredChanges && (
              <p className="mt-2 rounded border border-border bg-surface px-2.5 py-2 text-sm whitespace-pre-wrap text-text">
                {review.requiredChanges}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ---- Modals -------------------------------------------------------- */

function EditTaskModal({ task, open, onClose }: { task: TaskDetail; open: boolean; onClose: () => void }) {
  const { can } = useSession();
  const reference = useReferenceData(open);
  const update = useUpdateTask(task.reference);

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [priority, setPriority] = useState(task.priority);
  const [projectId, setProjectId] = useState(task.project?.id ?? '');
  const [categoryId, setCategoryId] = useState(task.category?.id ?? '');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${formatTaskReference(task.reference)}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form="edit-task-form"
            type="submit"
            variant="primary"
            loading={update.isPending}
          >
            Save changes
          </Button>
        </>
      }
    >
      <form
        id="edit-task-form"
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate(
            {
              title: title.trim(),
              description: description.trim(),
              priority,
              projectId: projectId || null,
              categoryId: categoryId || null,
            },
            { onSuccess: onClose },
          );
        }}
      >
        <TextInput label="Title" required value={title} onChange={(event) => setTitle(event.target.value)} />
        <TextArea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          className="min-h-[160px]"
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Priority"
            value={priority}
            onChange={(event) => setPriority(event.target.value as typeof priority)}
            options={(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((value) => ({
              value,
              label: value.charAt(0) + value.slice(1).toLowerCase(),
              disabled: value === 'CRITICAL' && !can('task:priority:critical'),
            }))}
          />
          <Select
            label="Project"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            placeholder="No project"
            options={(reference.data?.projects ?? []).map((project) => ({ value: project.id, label: project.name }))}
          />
          <Select
            label="Category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            placeholder="No category"
            options={(reference.data?.categories ?? []).map((category) => ({
              value: category.id,
              label: category.name,
            }))}
          />
        </div>
        <InlineError error={update.error} />
      </form>
    </Modal>
  );
}

function AssignModal({ task, open, onClose }: { task: TaskDetail; open: boolean; onClose: () => void }) {
  const reference = useReferenceData(open);
  const assign = useAssignTask(task.reference);
  const [assigneeId, setAssigneeId] = useState(task.assignee?.id ?? '');
  const [note, setNote] = useState('');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reassign this task"
      description="The new developer is sent a Discord DM. Reassigning a claimed task returns it to New."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form="assign-form"
            type="submit"
            variant="primary"
            loading={assign.isPending}
            disabled={assigneeId === (task.assignee?.id ?? '')}
          >
            Reassign
          </Button>
        </>
      }
    >
      <form
        id="assign-form"
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          assign.mutate({ assigneeId: assigneeId || null, note: note.trim() || null }, { onSuccess: onClose });
        }}
      >
        <Select
          label="Developer"
          value={assigneeId}
          onChange={(event) => setAssigneeId(event.target.value)}
          placeholder="Unassigned"
          options={(reference.data?.assignableUsers ?? []).map((user) => ({
            value: user.id,
            label: `${user.displayName} — ${user.appRole.replace('_', ' ').toLowerCase()}`,
          }))}
        />
        <TextArea
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Any context they should have before starting…"
        />
        <InlineError error={assign.error} />
      </form>
    </Modal>
  );
}

function DeleteModal({ task, open, onClose }: { task: TaskDetail; open: boolean; onClose: () => void }) {
  const remove = useDeleteTask(task.reference);
  const [reason, setReason] = useState('');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Delete ${formatTaskReference(task.reference)}?`}
      description="The task is archived rather than destroyed. Directors can still find and restore it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form="delete-form"
            type="submit"
            variant="danger"
            loading={remove.isPending}
            disabled={reason.trim().length < 3}
          >
            Delete task
          </Button>
        </>
      }
    >
      <form
        id="delete-form"
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          remove.mutate({ reason: reason.trim() }, { onSuccess: onClose });
        }}
      >
        <TextArea
          label="Why is this being deleted?"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded permanently in the audit log and posted to Discord."
        />
        <InlineError error={remove.error} />
      </form>
    </Modal>
  );
}
